import {
  base_colorContentBorder,
  base_colorSurface,
  tag_colorTagSevereText,
  text_colorTextPrimary,
  text_colorTextSecondary,
} from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import {
  font_4xl,
  font_family_weight_medium,
  font_family_weight_regular,
  font_md,
} from 'ext-ui/style/typography'
import { css } from 'styled-components'

const paddingHorizontal = spacing(10)
const footerPaddingHorizontal = spacing(5)

/*
  BaseFlyout has no pinned-footer / stepper slots (DS gap, flagged in the PR).
  Following the AssignAccountOwnerFlyout workaround: make Content + Body flex
  columns and clip their overflow so the header + footer stay fixed while only
  the field list scrolls. `&.AddVendorFlyout` scopes to this flyout's portal
  (the css-prop class and the AddVendorFlyout class land on the same element).
*/
export default css`
  &.AddVendorFlyout {
    .BaseFlyout-Content {
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    .BaseFlyout-Body {
      padding: 0;
      flex: auto;
      min-height: 0;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }
  }
`

/* Title + description block with an inset bottom divider. */
export const header = css`
  position: relative;
  padding: ${spacing(6)} ${paddingHorizontal};
  display: flex;
  flex-direction: column;
  gap: ${spacing(1)};

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: ${paddingHorizontal};
    right: ${paddingHorizontal};
    height: 1px;
    background-color: ${base_colorContentBorder};
  }
`

export const flyoutTitle = css`
  margin: 0;
  font-family: ${font_family_weight_medium};
  /* Stop the browser layering a synthetic bold over Rubik-Medium's own weight
     — the prototype's flyout titles are Title Case medium, not bold. */
  font-weight: 500;
  ${font_4xl}
  color: ${text_colorTextPrimary};
`

export const description = css`
  margin: 0;
  ${font_md}
  font-family: ${font_family_weight_regular};
  color: ${text_colorTextPrimary};
`

/* The form fills the remaining flyout height: scrollable field list + a pinned
   footer that stays put while the fields scroll. */
export const form = css`
  display: flex;
  flex-direction: column;
  flex: auto;
  min-height: 0;
`

export const confirmationSurface = css`
  display: flex;
  flex-direction: column;
  flex: auto;
  min-height: 0;
`

export const fields = css`
  flex: auto;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${spacing(4)};
  padding: ${spacing(6)} ${paddingHorizontal};
`

export const contactNameLabelOrder = css`
  .label-field {
    > *:nth-last-child(1) {
      order: 1;
    }
    > *:nth-last-child(2) {
      order: 2;
    }
  }
`

export const phoneField = css`
  [role='alert'] span {
    font-family: ${font_family_weight_regular} !important;
  }

  input::placeholder {
    opacity: 1 !important;
    color: ${text_colorTextSecondary} !important;
  }
`

export const phoneRow = css`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`

export const phoneInput = css`
  flex-grow: 2;
`

export const footer = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing(3)};
  padding: ${spacing(6)} ${footerPaddingHorizontal};
  border-top: 1px solid ${base_colorContentBorder};
  background: ${base_colorSurface};
`

export const footerButtons = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

/* No dedicated `text_colorTextError` token exists in the shipping DS (only
   input_borderError / tag severe / warning families) — using the severe tag
   text token; DS gap flagged owner-tagged in the PR. Error is never conveyed
   by color alone: it is a role="alert" text message. */
export const footerError = css`
  margin: 0;
  ${font_md}
  color: ${tag_colorTagSevereText};
`

export const step2Placeholder = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing(3)};
  padding: ${spacing(8)} ${paddingHorizontal};
`

export const pendingNote = css`
  margin: ${spacing(3)} 0 0;
  ${font_md}
  color: ${text_colorTextSecondary};
`

/* Acknowledgement footer. Prototype declared values: outer gap-[20px], row
   gap-[12px], text-[14px] leading-[20px], label font-medium on primary text,
   description on secondary text. */
export const ackBlock = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing(5)};
`

export const ackRow = css`
  display: flex;
  align-items: flex-start;
  gap: ${spacing(3)};
`

export const ackText = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing(1)};
  font-size: 14px;
  line-height: 20px;
`

export const ackPrimary = css`
  font-family: ${font_family_weight_medium};
  color: ${text_colorTextPrimary};
`

export const ackDescription = css`
  color: ${text_colorTextSecondary};
`

export const footerActionsEnd = css`
  display: flex;
  justify-content: flex-end;
`
